<script setup lang="ts">
import "leaflet/dist/leaflet.css";
import L, { control } from "leaflet";
import { BiomeLayer } from "../MapLayers/BiomeLayer";
import { Graticule } from "../MapLayers/Graticule";
import { computed, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue';
import BiomeTooltip from './BiomeTooltip.vue';
import { BlockPos, Chunk, ChunkPos, DensityFunction, Identifier, RandomState, Structure, StructurePlacement, StructureSet, WorldgenStructure } from 'deepslate';
import YSlider from './YSlider.vue';
import { useSearchStore } from '../stores/useBiomeSearchStore';
import { useSettingsStore } from '../stores/useSettingsStore';
import { useLoadedDimensionStore } from '../stores/useLoadedDimensionStore'
import { CachedBiomeSource } from '../util/CachedBiomeSource';
import MapButton from './MapButton.vue';
import { SpawnTarget } from '../util/SpawnTarget';
import { useI18n } from 'vue-i18n';
import { versionMetadata } from "../util";
import { formatCoordinateCopy, getDefaultCoordinateText, type CopyCoordinates } from '../util/copyCoordinates';

type SavedMapView = {
    centerX: number,
    centerZ: number,
    zoom: number,
    y: number
}

type ExploredStructureTarget = {
    key: string,
    marker: L.Marker
}

const MAP_VIEW_STORAGE_KEY = 'mc-datapack-map.map-view.v1'
const EXPLORED_STRUCTURES_STORAGE_KEY = 'mc-datapack-map.explored-structures.v1'
const COORDINATE_INPUT_STORAGE_KEY = 'mc-datapack-map.coordinate-input.v1'
const COPY_COORDINATE_MODE_STORAGE_KEY = 'mc-datapack-map.copy-coordinate-mode.v1'
const COPY_COORDINATE_TEMPLATE_STORAGE_KEY = 'mc-datapack-map.copy-coordinate-template.v1'
const MIN_MAP_ZOOM = -6
const MAX_MAP_ZOOM = 1
const EXPLORED_MARKER_OPACITY = 0.32

type CopyCoordinateMode = 'overworld' | 'nether'

function loadMapView(): SavedMapView | undefined {
    try {
        const stored = JSON.parse(localStorage.getItem(MAP_VIEW_STORAGE_KEY) ?? 'null') as Partial<SavedMapView> | null
        if (stored === null) return undefined
        if (![stored.centerX, stored.centerZ, stored.zoom, stored.y].every(value => typeof value === 'number' && Number.isFinite(value))) {
            return undefined
        }

        return {
            centerX: stored.centerX!,
            centerZ: stored.centerZ!,
            zoom: Math.min(Math.max(stored.zoom!, MIN_MAP_ZOOM), MAX_MAP_ZOOM),
            y: stored.y!
        }
    } catch {
        return undefined
    }
}

function loadExploredStructures(): Set<string> {
    try {
        const stored = JSON.parse(localStorage.getItem(EXPLORED_STRUCTURES_STORAGE_KEY) ?? '[]') as unknown
        if (!Array.isArray(stored)) return new Set()
        return new Set(stored.filter((value): value is string => typeof value === 'string'))
    } catch {
        return new Set()
    }
}

function loadCoordinateInput(): string {
    try {
        return localStorage.getItem(COORDINATE_INPUT_STORAGE_KEY)?.trim() ?? ''
    } catch {
        return ''
    }
}

function loadCopyCoordinateMode(): CopyCoordinateMode {
    try {
        return localStorage.getItem(COPY_COORDINATE_MODE_STORAGE_KEY) === 'nether' ? 'nether' : 'overworld'
    } catch {
        return 'overworld'
    }
}

function loadCopyCoordinateTemplate(): string {
    try {
        return localStorage.getItem(COPY_COORDINATE_TEMPLATE_STORAGE_KEY) ?? ''
    } catch {
        return ''
    }
}

const saved_map_view = loadMapView()
const explored_structures = loadExploredStructures()

const searchStore = useSearchStore()
const settingsStore = useSettingsStore()
const loadedDimensionStore = useLoadedDimensionStore()
const i18n = useI18n()

let biomeLayer: BiomeLayer
let graticule: Graticule

const tooltip_left = ref(0)
const tooltip_top = ref(0)
const tooltip_biome = ref(Identifier.create("void"))
const tooltip_position = ref(BlockPos.ZERO)
const show_tooltip = ref(false)
const show_info = ref(false)

const do_hillshade = ref(true)
const show_sealevel = ref(false)
const project_down = ref(true)

const y = ref(saved_map_view?.y ?? 320)

const show_graticule = ref(false)
const show_structures_when_zoomed_out = ref(true)
const isFullscreen = ref(false)
const coordinate_input = ref(loadCoordinateInput())
const coordinate_input_element = ref<HTMLInputElement | null>(null)
const coordinate_copy_picker_element = ref<HTMLElement | null>(null)
const coordinate_copy_template_element = ref<HTMLInputElement | null>(null)
const coordinate_input_invalid = ref(false)
const coordinate_input_collapsed = ref(false)
const copy_coordinate_mode = ref<CopyCoordinateMode>(loadCopyCoordinateMode())
const copy_coordinate_template = ref(loadCopyCoordinateTemplate())
const copy_coordinate_menu_open = ref(false)

const is_overworld = computed(() => settingsStore.dimension.toString() === 'minecraft:overworld')
const copy_coordinate_mode_label = computed(() => {
    if (!is_overworld.value) {
        return i18n.t('map.copy_coordinate_format.title')
    }
    return i18n.t(`map.copy_coordinate_mode.${copy_coordinate_mode.value}`)
})

const low_zoom_structures_icon = computed(() => show_structures_when_zoomed_out.value ? 'fa-eye' : 'fa-eye-slash')
const low_zoom_structures_title = computed(() => i18n.t(
    show_structures_when_zoomed_out.value ? 'map.setting.structures_visible' : 'map.setting.structures_hidden'
))
const fullscreenIcon = computed(() => isFullscreen.value ? 'fa-compress' : 'fa-expand')
const fullscreenTitle = computed(() => i18n.t(
    isFullscreen.value ? 'map.fullscreen.exit' : 'map.fullscreen.enter'
))

watch(show_graticule, (value) => {
    if (value) {
        map.addLayer(graticule)
    } else {
        map.removeLayer(graticule)
    }
})

watch(show_structures_when_zoomed_out, () => {
    setTimeout(updateMarkers, 5)
})

watch(copy_coordinate_mode, (mode) => {
    try {
        localStorage.setItem(COPY_COORDINATE_MODE_STORAGE_KEY, mode)
    } catch {
        // Storage can be unavailable in private browsing or restricted contexts.
    }
})

watch(copy_coordinate_template, (template) => {
    try {
        localStorage.setItem(COPY_COORDINATE_TEMPLATE_STORAGE_KEY, template)
    } catch {
        // Storage can be unavailable in private browsing or restricted contexts.
    }
})

function toggleCopyCoordinateMenu() {
    copy_coordinate_menu_open.value = !copy_coordinate_menu_open.value
}

function selectCopyCoordinateMode(mode: CopyCoordinateMode) {
    copy_coordinate_mode.value = mode
    copy_coordinate_menu_open.value = false
}

function closeCopyCoordinateMenu(event: PointerEvent) {
    if (!coordinate_copy_picker_element.value?.contains(event.target as Node)) {
        copy_coordinate_menu_open.value = false
    }
}

var map: L.Map
var zoom: L.Control.Zoom
var markers: L.LayerGroup
var spawnMarker: L.Marker
var playerLocationMarker: L.CircleMarker | undefined
var mapResizeObserver: ResizeObserver | undefined
var mapResizeFrame: number | undefined
var mapResumeFrame: number | undefined

var marker_map = new Map<string, { marker?: L.Marker, structure?: {id: Identifier, pos: BlockPos}}>()
var needs_zoom = ref(false)



onMounted(() => {
    map = L.map("map", {
        zoom: saved_map_view?.zoom ?? -2,
        minZoom: MIN_MAP_ZOOM,
        maxZoom: MAX_MAP_ZOOM,
        center: saved_map_view ? [-saved_map_view.centerZ, saved_map_view.centerX] : [0, 0],
        zoomControl: false,
        crs: L.CRS.Simple
    })

    zoom = L.control.zoom({
        position: i18n.t('locale.text_direction') === 'ltr' ? 'topright' : 'topleft'
    })
    zoom.addTo(map)

    biomeLayer = new BiomeLayer({
            tileSize: 256,
            minZoom: -100
        },
        do_hillshade,
        show_sealevel,
        project_down,
        y
    )

    map.addLayer(biomeLayer)

    const mapContainer = document.getElementById("map_container")
    if (mapContainer) {
        mapResizeObserver = new ResizeObserver(() => {
            cancelAnimationFrame(mapResizeFrame!)
            mapResizeFrame = requestAnimationFrame(() => {
                if (map !== undefined) {
                    map.invalidateSize({ pan: false })
                }
            })
        })
        mapResizeObserver.observe(mapContainer)
    }

    spawnMarker = L.marker({lat: 0, lng: 0}, {
        icon: L.icon({
            iconUrl: "images/spawn_icon.png",
            iconAnchor: [16, 16],
            popupAnchor: [0, -10]
        }),
    }).bindPopup(L.popup())

    updateSpawnMarker()

    markers = L.layerGroup().addTo(map)

    map.addEventListener("mousemove", (evt: L.LeafletMouseEvent) => {
        //        await datapackStore.registered
        tooltip_left.value = evt.containerPoint.x - 4
        tooltip_top.value = evt.containerPoint.y - 4

        const pos = getPosition(map, evt.latlng)

        const biome = loadedDimensionStore.getBiomeSource()?.getBiome(pos[0] >> 2, pos[1] >> 2, pos[2] >> 2, loadedDimensionStore.sampler) ?? Identifier.create("plains")

        tooltip_biome.value = biome
        tooltip_position.value = pos
        show_tooltip.value = true
    })


    map.addEventListener("mouseout", (evt: L.LeafletMouseEvent) => {
        show_tooltip.value = false
    })

    map.addEventListener("contextmenu", async (evt: L.LeafletMouseEvent) => {
        const pos = getPosition(map, evt.latlng)
        const copyY = pos[1] + (project_down.value ? 10 : 0)
        const coordinates: CopyCoordinates = {
            x: pos[0],
            y: copyY,
            z: pos[2]
        }
        const defaultText = `/execute in ${settingsStore.dimension.toString()} run tp ${coordinates.x.toFixed(0)} ${copyY.toFixed(0)} ${coordinates.z.toFixed(0)}`
        navigator.clipboard.writeText(formatCoordinateCopy(coordinates, defaultText, copy_coordinate_template.value))
        show_info.value = true
        setTimeout(() => show_info.value = false, 2000)
    })


    map.on("moveend", (evt) => {
        setTimeout(updateMarkers, 5)
        saveMapView()
    })

    graticule = new Graticule()

    /*
    layer.on("tileunload", (evt) => {
        // @ts-expect-error: _tileCoordsToBounds does not exist
        const tileBounds = layer._tileCoordsToBounds(evt.coords);

    })*/

    document.addEventListener("fullscreenchange", updateFullscreenState)
    document.addEventListener("visibilitychange", handleVisibilityChange)
    document.addEventListener("pointerdown", closeCopyCoordinateMenu)
    window.addEventListener("focus", resyncMapAfterResume)
    window.addEventListener("pageshow", resyncMapAfterResume)
});

watch(y, saveMapView)

onBeforeUnmount(() => {
    mapResizeObserver?.disconnect()
    if (mapResizeFrame !== undefined) {
        cancelAnimationFrame(mapResizeFrame)
    }
    if (mapResumeFrame !== undefined) {
        cancelAnimationFrame(mapResumeFrame)
    }
    document.removeEventListener("fullscreenchange", updateFullscreenState)
    document.removeEventListener("visibilitychange", handleVisibilityChange)
    document.removeEventListener("pointerdown", closeCopyCoordinateMenu)
    window.removeEventListener("focus", resyncMapAfterResume)
    window.removeEventListener("pageshow", resyncMapAfterResume)
})

watch(i18n.locale, () => {
    zoom.setPosition(i18n.t('locale.text_direction') === 'ltr' ? 'topright' : 'topleft')
})

function getPosition(map: L.Map, latlng: L.LatLng) {
    const crs = map.options.crs!
    const pos = crs.project(latlng)
    pos.y *= -1

    const surface = (loadedDimensionStore.surface_density_function)?.compute(DensityFunction.context((pos.x >> 2) << 2, y.value, (pos.y >> 2) << 2)) ?? Number.POSITIVE_INFINITY

    const pos_y: number = project_down.value ? Math.min(surface, y.value) : y.value
    return BlockPos.create(pos.x, pos_y, pos.y)
}

function isInBounds(pos: ChunkPos, min: ChunkPos, max: ChunkPos) {
    return (pos[0] >= min[0] && pos[0] <= max[0] && pos[1] >= min[1] && pos[1] <= max[1])
}


function updateMarkers() {
    const biomeSource = loadedDimensionStore.getBiomeSource()
    if (biomeSource === undefined) {
        return
    }

    const cachedBiomeSource = new CachedBiomeSource(biomeSource)
    const context = new WorldgenStructure.GenerationContext(settingsStore.seed, cachedBiomeSource, loadedDimensionStore.noise_generator_settings, loadedDimensionStore.loaded_dimension.level_height ?? {minY: 0, height: 256})

    const bounds = map.getBounds()

    const crs = map.options.crs!
    const minPos = crs.project(bounds.getNorthWest())
    const maxPos = crs.project(bounds.getSouthEast())

    const minChunk = ChunkPos.create(minPos.x >> 4, -minPos.y >> 4)
    const maxChunk = ChunkPos.create(maxPos.x >> 4, -maxPos.y >> 4)

    var _needs_zoom = false

    const keptMarkers: Set<string> = new Set()

    const scheduler = ('scheduler' in window) ? ((task: () => void) => (window as any).scheduler.postTask(task, {priority: "background"})) : ((task: () => void) => setTimeout(task, 1))

    for (const id of searchStore.structure_sets.sets) {
        const set = StructureSet.REGISTRY.get(id)
        if (!set) continue

        var minZoom = 2

        if (set.placement instanceof StructurePlacement.ConcentricRingsStructurePlacement){
            set.placement.prepare(biomeSource, loadedDimensionStore.sampler, settingsStore.seed)
            minZoom = -2
        } else if (set.placement instanceof StructurePlacement.RandomSpreadStructurePlacement) {
            const chunkFrequency = (set.placement.frequency) / (set.placement.spacing * set.placement.spacing)
            minZoom = -Math.log2(1/(chunkFrequency * 128))
        }

        if (show_structures_when_zoomed_out.value || map.getZoom() >= minZoom){
            const chunks: ChunkPos[] = set.placement.getPotentialStructureChunks(settingsStore.seed, minChunk[0], minChunk[1], maxChunk[0], maxChunk[1])

            for (const chunk of chunks) {
                const storage_id = `${id.toString()} ${chunk[0]},${chunk[1]}`
                const inBounds = isInBounds(chunk, minChunk, maxChunk)
                const stored = marker_map.get(storage_id)

                if (inBounds){
                    if (stored === undefined) {
                        const m: { marker?: L.Marker, structure?: {id: Identifier, pos: BlockPos} } = {}

                        marker_map.set(storage_id, m)


                        scheduler(() => {
                            if (marker_map.get(storage_id) !== m) return

                            cachedBiomeSource.setupCache(chunk[0] << 2, chunk[1] << 2)
                            const structure = set.getStructureInChunk(chunk[0], chunk[1], context)

                            const marker = structure && searchStore.structures.has(structure.id.toString()) ? getMarker(structure.id, structure.pos) : undefined
                            m.structure = structure
                            m.marker = marker
                        })
                    } else {
                        if (stored.structure){
                            const should_have_marker = searchStore.structures.has(stored.structure?.id.toString())
                            if (should_have_marker && stored.marker === undefined){
                                stored.marker = getMarker(stored.structure.id, stored.structure.pos)
                            } else if (!should_have_marker && stored.marker !== undefined){
                                stored.marker.remove()
                                stored.marker = undefined
                            }
                        }
                    }
                    keptMarkers.add(storage_id)
                }
            }
        } else {
            _needs_zoom = true
        }
    }

    for (const key of marker_map.keys()){
        if (!keptMarkers.has(key)){
            const marker = marker_map.get(key)
            marker?.marker?.remove()
            marker_map.delete(key)
        }
    }

    needs_zoom.value = _needs_zoom
}

function getMarker(structureId: Identifier, pos: BlockPos) {
    const crs = map.options.crs!
    const mapPos = new L.Point(pos[0], -pos[2])
    const exploredKey = getStructureMarkerKey(structureId, pos)
    const marker = L.marker(crs.unproject(mapPos))
    const netherCoordinates = getNetherCoordinates(pos[0], pos[2])
    const popup = L.popup({ maxWidth: 420 }).setContent(() => createCoordinatePopup(
        settingsStore.getLocalizedName("structure", structureId, false),
        i18n.t("map.coords.xyz", {x: pos[0], y: pos[1], z: pos[2]}),
        { x: pos[0], y: pos[1], z: pos[2] },
        netherCoordinates,
        { key: exploredKey, marker }
    ))
    marker.bindPopup(popup).addTo(markers)
    const iconUrl = loadedDimensionStore.getIcon(structureId)
    marker.setIcon(L.icon({
        iconUrl: iconUrl,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
        shadowUrl: 'shadow.png',
        shadowSize: [40, 40],
        shadowAnchor: [20, 20],
        popupAnchor: [0, -10]
    }))
    marker.setOpacity(explored_structures.has(exploredKey) ? EXPLORED_MARKER_OPACITY : 1)
    return marker
}

function getStructureMarkerKey(structureId: Identifier, pos: BlockPos) {
    return `${settingsStore.seed}|${settingsStore.dimension.toString()}|${structureId.toString()}|${pos[0]},${pos[1]},${pos[2]}`
}

function saveExploredStructures() {
    try {
        localStorage.setItem(EXPLORED_STRUCTURES_STORAGE_KEY, JSON.stringify([...explored_structures]))
    } catch {
        // Storage can be unavailable in private browsing or restricted contexts.
    }
}

function updateSpawnMarker(){
    if (settingsStore.dimension.toString() === "minecraft:overworld"){
        const crs = map.options.crs!
        const spawnTarget = SpawnTarget.fromJson(loadedDimensionStore.loaded_dimension.noise_settings_json?.spawn_target, versionMetadata[settingsStore.mc_version].spawnAlgorithm)
        const spawn = spawnTarget.getSpawnPoint(loadedDimensionStore.sampler)
        const pos = new L.Point(spawn[0] + 7, - spawn[1] - 7)
        const spawnX = spawn[0] + 7
        const spawnZ = spawn[1] + 7
        const netherCoordinates = getNetherCoordinates(spawnX, spawnZ)
        spawnMarker.setLatLng(crs.unproject(pos))
        spawnMarker.bindPopup(L.popup().setContent(() => createCoordinatePopup(
            i18n.t("map.tooltip.spawn"),
            i18n.t("map.coords.xz", {x: spawnX, z: spawnZ}),
            { x: spawnX, z: spawnZ },
            netherCoordinates
        )))
        spawnMarker.addTo(map)
    } else {
        spawnMarker.removeFrom(map)
    }

}

// The Nether shares the Overworld's X/Z axes at a 1:8 scale, so a map position
// can also be copied as the matching Nether coordinates.
function getNetherCoordinates(x: number, z: number): CopyCoordinates | undefined {
    if (!is_overworld.value) {
        return undefined
    }

    return { x: Math.round(x / 8), y: 128, z: Math.round(z / 8) }
}

function createCoordinatePopup(title: string, coordinates: string, copyCoordinates: CopyCoordinates, netherCoordinates?: CopyCoordinates, exploredTarget?: ExploredStructureTarget) {
    const container = document.createElement("div")
    container.className = "coordinate_popup"

    const heading = document.createElement("div")
    heading.className = "coordinate_popup_title"
    heading.textContent = title

    const row = document.createElement("div")
    row.className = "coordinate_popup_row"

    const coordinateText = document.createElement("span")
    coordinateText.textContent = coordinates

    const copyButton = document.createElement("button")
    copyButton.type = "button"
    copyButton.className = "coordinate_popup_copy"
    const usesNetherCoordinates = copy_coordinate_mode.value === 'nether' && netherCoordinates !== undefined
    copyButton.title = i18n.t(usesNetherCoordinates ? "map.copy_nether_coordinates.title" : "map.copy_coordinates.title")
    copyButton.textContent = i18n.t("map.copy_coordinates")
    copyButton.addEventListener("click", async () => {
        const selectedCoordinates = copy_coordinate_mode.value === 'nether' && netherCoordinates !== undefined
            ? netherCoordinates
            : copyCoordinates
        const defaultText = getDefaultCoordinateText(
            selectedCoordinates,
            selectedCoordinates === netherCoordinates ? ' ' : ', '
        )
        const selectedClipboardText = formatCoordinateCopy(selectedCoordinates, defaultText, copy_coordinate_template.value)
        try {
            await navigator.clipboard.writeText(selectedClipboardText)
            copyButton.textContent = i18n.t("map.coordinates_copied")
            setTimeout(() => copyButton.textContent = i18n.t("map.copy_coordinates"), 1200)
        } catch {
            // Clipboard access can be unavailable outside a secure context.
        }
    })

    row.append(coordinateText, copyButton)

    if (exploredTarget) {
        const exploreButton = document.createElement("button")
        exploreButton.type = "button"
        exploreButton.className = "coordinate_popup_explore"
        const updateExploreButton = () => {
            const isExplored = explored_structures.has(exploredTarget.key)
            exploreButton.classList.toggle("active", isExplored)
            exploreButton.setAttribute("aria-pressed", String(isExplored))
            exploreButton.title = i18n.t(isExplored ? "map.explored.remove_title" : "map.explored.title")
            exploreButton.textContent = i18n.t("map.explored.label")
        }
        exploreButton.addEventListener("click", () => {
            const shouldExplore = !explored_structures.has(exploredTarget.key)
            if (shouldExplore) {
                explored_structures.add(exploredTarget.key)
            } else {
                explored_structures.delete(exploredTarget.key)
            }
            exploredTarget.marker.setOpacity(shouldExplore ? EXPLORED_MARKER_OPACITY : 1)
            saveExploredStructures()
            updateExploreButton()
        })
        updateExploreButton()
        row.append(exploreButton)
    }

    container.append(heading, row)
    return container
}

function zoomInToShowStructures() {
    if (map !== undefined) {
        map.zoomIn()
    }
}

function updateFullscreenState() {
    const mapContainer = document.getElementById("map_container")
    isFullscreen.value = document.fullscreenElement === mapContainer

    requestAnimationFrame(() => {
        if (map !== undefined) {
            map.invalidateSize({ pan: false })
        }
    })
}

// While the page is hidden (tab switch, minimized window, a fullscreen game on
// top) the browser stops running animation frames, can hand Leaflet a stale
// container size and may drop the pixels of the tile canvases. The map then
// comes back blank until a reload, so re-measure it and let the layer repaint
// itself whenever the page becomes visible again.
function resyncMapAfterResume() {
    if (map === undefined) return

    cancelAnimationFrame(mapResumeFrame!)
    mapResumeFrame = requestAnimationFrame(() => {
        if (map === undefined) return

        map.invalidateSize({ pan: false })
        biomeLayer?.resyncAfterResume()
    })
}

function handleVisibilityChange() {
    if (document.visibilityState === "visible") {
        resyncMapAfterResume()
    }
}

async function toggleFullscreen() {
    const mapContainer = document.getElementById("map_container")
    if (!mapContainer) return

    try {
        if (document.fullscreenElement) {
            await document.exitFullscreen()
        } else {
            await mapContainer.requestFullscreen()
        }
    } catch {
        // Fullscreen access can be unavailable in restricted browser contexts.
    }
}

function parseCoordinates(value: string): {x: number, y?: number, z: number} | undefined {
    const matches = value.match(/[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/g)
    if (matches === null) return undefined

    const coordinates = matches.map(Number).filter(Number.isFinite)
    if (coordinates.length < 2) return undefined

    if (coordinates.length === 2){
        return {x: coordinates[0], z: coordinates[1]}
    }

    return {x: coordinates[0], y: coordinates[1], z: coordinates[2]}
}

function jumpToCoordinates() {
    const coordinates = parseCoordinates(coordinate_input.value)
    if (coordinates === undefined || map === undefined){
        coordinate_input_invalid.value = true
        return
    }

    coordinate_input_invalid.value = false
    coordinate_input.value = coordinate_input.value.trim()
    saveCoordinateInput()
    if (coordinates.y !== undefined){
        y.value = coordinates.y
    }

    const target = map.options.crs!.unproject(new L.Point(coordinates.x, -coordinates.z))
    updatePlayerLocationMarker(target, coordinates.x, coordinates.z)
    map.setView(target, map.getZoom(), {animate: true})
    collapseCoordinateInput()
}

function updatePlayerLocationMarker(target: L.LatLng, x: number, z: number) {
    const label = i18n.t("map.coords.xz", {
        x,
        z
    })

    if (playerLocationMarker === undefined) {
        playerLocationMarker = L.circleMarker(target, {
            radius: 5,
            color: '#2f6b43',
            weight: 2,
            fillColor: '#ffffff',
            fillOpacity: 1,
            className: 'player_location_marker'
        }).addTo(map)
        playerLocationMarker.bindTooltip(label, {
            permanent: true,
            direction: 'top',
            offset: [0, -5],
            opacity: 1,
            interactive: false,
            className: 'player_location_label'
        })
        return
    }

    playerLocationMarker.setLatLng(target)
    playerLocationMarker.setTooltipContent(label)
}

function saveCoordinateInput() {
    try {
        localStorage.setItem(COORDINATE_INPUT_STORAGE_KEY, coordinate_input.value)
    } catch {
        // Storage can be unavailable in private browsing or restricted contexts.
    }
}

function collapseCoordinateInput() {
    coordinate_input_collapsed.value = true

    const activeElement = document.activeElement
    if (activeElement instanceof HTMLElement && activeElement.closest('.coordinate_jump')) {
        activeElement.blur()
    }
}

async function pasteCoordinates() {
    try {
        coordinate_input.value = (await navigator.clipboard.readText()).trim()
        coordinate_input_invalid.value = false
        coordinate_input_element.value?.focus()
    } catch {
        coordinate_input_invalid.value = true
    }
}

function saveMapView() {
    if (map === undefined) return

    const center = map.options.crs!.project(map.getCenter())
    const saved: SavedMapView = {
        centerX: center.x,
        centerZ: -center.y,
        zoom: map.getZoom(),
        y: y.value
    }

    try {
        localStorage.setItem(MAP_VIEW_STORAGE_KEY, JSON.stringify(saved))
    } catch {
        // Storage can be unavailable in private browsing or restricted contexts.
    }
}

loadedDimensionStore.$subscribe((mutation, state) => {
    for (const marker of marker_map.values()){
        marker.marker?.remove()
    }
    marker_map.clear()
    playerLocationMarker?.removeFrom(map)
    playerLocationMarker = undefined
    updateMarkers()
    updateSpawnMarker()

    const level_height = loadedDimensionStore.loaded_dimension.level_height
    if (level_height){
        if (project_down.value && loadedDimensionStore.surface_density_function !== undefined){
            y.value = level_height.minY + level_height.height || y.value
        } else {
            y.value = Math.max(Math.min(y.value, level_height.minY + level_height.height), level_height.minY)
        }
    } 
})

watch(searchStore.structures, () => {
    updateMarkers()
})

</script>
  
<template>
    <div id="map_container">
        <div id="map">
        </div>
        <div class="map_options">
            <div
                class="coordinate_jump"
                :class="{ invalid: coordinate_input_invalid, collapsed: coordinate_input_collapsed }"
                @focusin="coordinate_input_collapsed = false"
                @mouseleave="coordinate_input_collapsed = false"
            >
                <button
                    class="coordinate_jump_toggle"
                    type="button"
                    :title="i18n.t('map.coordinate_input.title')"
                    :aria-label="i18n.t('map.coordinate_input.title')"
                    @click="coordinate_input_element?.focus()"
                >
                    <font-awesome-icon icon="fa-location-dot" />
                </button>
                <div class="coordinate_jump_fields">
                    <input
                        ref="coordinate_input_element"
                        v-model="coordinate_input"
                        type="text"
                        inputmode="decimal"
                        spellcheck="false"
                        :aria-label="i18n.t('map.coordinate_input.title')"
                        :placeholder="i18n.t('map.coordinate_input.placeholder')"
                        @input="coordinate_input_invalid = false"
                        @keydown.enter.prevent="jumpToCoordinates"
                        @keydown.escape.prevent="collapseCoordinateInput"
                    />
                    <button
                        class="coordinate_jump_paste"
                        type="button"
                        :title="i18n.t('map.coordinate_input.paste')"
                        :aria-label="i18n.t('map.coordinate_input.paste')"
                        @click="pasteCoordinates"
                    >
                        <font-awesome-icon icon="fa-paste" />
                    </button>
                    <button
                        class="coordinate_jump_go"
                        type="button"
                        :title="i18n.t('map.coordinate_input.title')"
                        :aria-label="i18n.t('map.coordinate_input.title')"
                        @click="jumpToCoordinates"
                    >
                        <font-awesome-icon icon="fa-location-dot" />
                    </button>
                </div>
            </div>
            <div
                ref="coordinate_copy_picker_element"
                class="coordinate_copy_mode_picker"
                :class="{
                    active: copy_coordinate_template.trim() !== '',
                    open: copy_coordinate_menu_open
                }"
                @keydown.esc.stop.prevent="copy_coordinate_menu_open = false"
            >
                <button
                    class="coordinate_copy_mode_toggle"
                    type="button"
                    :title="i18n.t('map.copy_coordinate_mode.title')"
                    :aria-label="i18n.t('map.copy_coordinate_mode.title')"
                    aria-haspopup="menu"
                    :aria-expanded="copy_coordinate_menu_open"
                    @click="toggleCopyCoordinateMenu"
                >
                    <font-awesome-icon icon="fa-copy" />
                </button>
                <button
                    class="coordinate_copy_mode"
                    type="button"
                    :aria-label="i18n.t('map.copy_coordinate_mode.title')"
                    aria-haspopup="menu"
                    aria-controls="coordinate-copy-menu"
                    :aria-expanded="copy_coordinate_menu_open"
                    @click="toggleCopyCoordinateMenu"
                >
                    <span>{{ copy_coordinate_mode_label }}</span>
                    <font-awesome-icon icon="fa-angle-down" class="coordinate_copy_mode_chevron" />
                </button>
                <div v-if="copy_coordinate_menu_open" id="coordinate-copy-menu" class="coordinate_copy_menu" role="menu">
                    <template v-if="is_overworld">
                        <button
                            class="coordinate_copy_menu_item"
                            :class="{ selected: copy_coordinate_mode === 'overworld' }"
                            type="button"
                            role="menuitemradio"
                            :aria-checked="copy_coordinate_mode === 'overworld'"
                            @click="selectCopyCoordinateMode('overworld')"
                        >
                            <span class="coordinate_copy_menu_check" aria-hidden="true">{{ copy_coordinate_mode === 'overworld' ? '✓' : '' }}</span>
                            <span>{{ i18n.t('map.copy_coordinate_mode.overworld') }}</span>
                        </button>
                        <button
                            class="coordinate_copy_menu_item"
                            :class="{ selected: copy_coordinate_mode === 'nether' }"
                            type="button"
                            role="menuitemradio"
                            :aria-checked="copy_coordinate_mode === 'nether'"
                            @click="selectCopyCoordinateMode('nether')"
                        >
                            <span class="coordinate_copy_menu_check" aria-hidden="true">{{ copy_coordinate_mode === 'nether' ? '✓' : '' }}</span>
                            <span>{{ i18n.t('map.copy_coordinate_mode.nether') }}</span>
                        </button>
                        <div class="coordinate_copy_menu_divider" role="separator"></div>
                    </template>
                    <div class="coordinate_copy_menu_format">
                        <label for="coordinate-copy-template">{{ i18n.t('map.copy_coordinate_format.title') }}</label>
                        <input
                            id="coordinate-copy-template"
                            ref="coordinate_copy_template_element"
                            v-model="copy_coordinate_template"
                            type="text"
                            maxlength="500"
                            spellcheck="false"
                            autocomplete="off"
                            :title="i18n.t('map.copy_coordinate_format.help')"
                            :aria-label="i18n.t('map.copy_coordinate_format.title')"
                            :placeholder="i18n.t('map.copy_coordinate_format.placeholder')"
                            @keydown.esc.stop.prevent="copy_coordinate_menu_open = false"
                        />
                        <div class="coordinate_copy_menu_hint">{{ i18n.t('map.copy_coordinate_format.help') }}</div>
                    </div>
                </div>
            </div>
            <MapButton :icon="fullscreenIcon" :model-value="isFullscreen" :title="fullscreenTitle" @update:model-value="toggleFullscreen" />
            <Suspense>
                <YSlider class="slider" v-model:y="y" />
            </Suspense>
            <MapButton icon="fa-arrows-down-to-line" :disabled="loadedDimensionStore.surface_density_function === undefined" v-model="project_down" :title="i18n.t('map.setting.project')" />
            <MapButton icon="fa-mountain-sun" :disabled="(!project_down || loadedDimensionStore.surface_density_function === undefined) && ! loadedDimensionStore.terrain_density_function" v-model="do_hillshade"  :title="i18n.t('map.setting.hillshade')" />
            <MapButton icon="fa-water" :disabled="loadedDimensionStore.surface_density_function === undefined" v-model="show_sealevel" :title="i18n.t('map.setting.sealevel')" />
            <MapButton icon="fa-table-cells" v-model="show_graticule" :title="i18n.t('map.setting.graticule')" />
            <MapButton :icon="low_zoom_structures_icon" v-model="show_structures_when_zoomed_out" :title="low_zoom_structures_title" />
        </div>
        <BiomeTooltip id="tooltip" v-if="show_tooltip" :style="{ left: tooltip_left + 'px', top: tooltip_top + 'px' }"
            :biome="tooltip_biome" :pos="tooltip_position" />
        <div class="top">
            <Transition>
                <button class="info zoom" type="button" v-if="needs_zoom" @click="zoomInToShowStructures">
                    <font-awesome-icon icon="fa-magnifying-glass-plus" />
                    <span>{{ i18n.t('map.info.structures_hidden') }}</span>
                </button>
            </Transition>
            <Transition>
                <div class="info unsupported" v-if="searchStore.structure_sets.has_invalid">
                    {{ i18n.t('map.error.structures_unsupported') }}
                </div>
            </Transition>
        </div>
        <Transition>
            <div class="info bottom teleport" v-if="show_info">
                {{ i18n.t('map.info.teleport_command_copied') }}
            </div>
        </Transition>
    </div>
</template>

<style scoped>
#map_container {
    width: 100%;
    height: 100%;
    position: relative;
}

#map {
    width: 100%;
    height: 100%;
    cursor: crosshair;
    background: white url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill-opacity=".25" ><rect x="20" width="20" height="20" /><rect y="20" width="20" height="20" /></svg>');
    background-size: 25px 25px;
}

#map_container:fullscreen {
    width: 100vw;
    height: 100vh;
    background: #ffffff;
}

#map.leaflet-drag-target {
    cursor: grab;
}

#map_container :deep(.leaflet-control-zoom) {
    border: 0;
    border-radius: 7px;
    overflow: hidden;
    box-shadow: 0 3px 14px rgba(15, 23, 42, 0.18);
}

#map_container :deep(.leaflet-control-zoom a) {
    width: 1.6rem;
    height: 1.6rem;
    line-height: 1.6rem;
    font-size: 0.85rem;
    background: #ffffff;
    color: #334155;
    border-bottom-color: #e2e8f0;
}

#map_container :deep(.leaflet-control-zoom a:hover) {
    background: #f1f5f9;
}

#map_container :deep(.leaflet-popup-content-wrapper),
#map_container :deep(.leaflet-popup-tip) {
    background: #ffffff;
    color: #0f172a;
}

#map_container :deep(.coordinate_popup_title) {
    margin-bottom: 0.25rem;
}

#map_container :deep(.coordinate_popup_row) {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    white-space: nowrap;
}

#map_container :deep(.coordinate_popup_copy),
#map_container :deep(.coordinate_popup_explore) {
    padding: 0.15rem 0.4rem;
    border: 1px solid #94a3b8;
    border-radius: 4px;
    background: #ffffff;
    color: #334155;
    font: inherit;
    line-height: 1.2;
    min-width: 3.6rem;
    text-align: center;
    cursor: pointer;
}

#map_container :deep(.coordinate_popup_copy:hover),
#map_container :deep(.coordinate_popup_explore:hover) {
    background: #e8f4ec;
    border-color: #8fc39e;
    color: #2f6b43;
}

#map_container :deep(.coordinate_popup_explore.active) {
    background: #dff0e4;
    border-color: #4d8c61;
    color: #2f6b43;
    font-weight: 700;
}

.map_options {
    position: absolute;
    z-index: 600;
    top: 4.8rem;
    right: 0.55rem;
    display: flex;
    flex-direction: column;
    align-items: end;
    gap: 0.35rem;
}

.map_options:dir(rtl) {
    right: unset;
    left: 0.55rem;
}

.coordinate_jump {
    display: flex;
    align-items: center;
    width: 1.6rem;
    max-width: calc(100vw - 1.1rem);
    height: 1.6rem;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
    overflow: hidden;
    transition: width 160ms ease, border-color 160ms ease, box-shadow 160ms ease;
}

.coordinate_jump:hover,
.coordinate_jump:focus-within {
    width: min(14rem, calc(100vw - 1.1rem));
}

.coordinate_jump.collapsed,
.coordinate_jump.collapsed:hover,
.coordinate_jump.collapsed:focus-within {
    width: 1.6rem;
}

.coordinate_jump:focus-within {
    border-color: #4d8c61;
    box-shadow: 0 0 0 3px rgba(77, 140, 97, 0.14);
}

.coordinate_jump.invalid {
    border-color: #c24141;
    box-shadow: 0 0 0 3px rgba(194, 65, 65, 0.14);
}

.coordinate_copy_mode_picker {
    position: relative;
    display: flex;
    align-items: center;
    width: 1.6rem;
    max-width: calc(100vw - 1.1rem);
    height: 1.6rem;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
    overflow: visible;
    transition: width 160ms ease, border-color 160ms ease, box-shadow 160ms ease;
}

.coordinate_copy_mode_picker:hover,
.coordinate_copy_mode_picker:focus-within,
.coordinate_copy_mode_picker.open {
    width: min(7.6rem, calc(100vw - 1.1rem));
    border-color: #94a3b8;
}

.coordinate_copy_mode_picker:focus-within,
.coordinate_copy_mode_picker.open {
    border-color: #4d8c61;
    box-shadow: 0 0 0 3px rgba(77, 140, 97, 0.14);
}

.coordinate_copy_mode_picker.active {
    border-color: #8fc39e;
}

.coordinate_copy_mode_toggle {
    width: 1.6rem;
    height: 100%;
    flex: 0 0 1.6rem;
    padding: 0;
    border: 0;
    background: #f8fafc;
    color: #2f6b43;
    cursor: pointer;
}

.coordinate_copy_mode_picker.active .coordinate_copy_mode_toggle {
    background: #e8f4ec;
}

.coordinate_copy_mode_toggle:hover,
.coordinate_copy_mode_toggle:focus-visible {
    background: #e8f4ec;
    outline: none;
}

.coordinate_copy_mode {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    min-width: 0;
    width: 100%;
    height: 100%;
    flex: 1 1 auto;
    overflow: hidden;
    padding: 0 0.25rem;
    border: 0;
    border-left: 1px solid #e2e8f0;
    outline: none;
    background: #ffffff;
    color: #334155;
    font: inherit;
    font-size: 0.72rem;
    cursor: pointer;
    opacity: 0;
    pointer-events: none;
    transition: opacity 120ms ease;
}

.coordinate_copy_mode span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.coordinate_copy_mode_chevron {
    flex: 0 0 auto;
    font-size: 0.65rem;
}

.coordinate_copy_mode_picker:hover .coordinate_copy_mode,
.coordinate_copy_mode_picker:focus-within .coordinate_copy_mode,
.coordinate_copy_mode_picker.open .coordinate_copy_mode {
    opacity: 1;
    pointer-events: auto;
}

.coordinate_copy_menu {
    position: absolute;
    z-index: 800;
    top: calc(100% + 0.35rem);
    right: 0;
    width: min(18rem, calc(100vw - 1.1rem));
    padding: 0.35rem;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.2);
    box-sizing: border-box;
}

.coordinate_copy_mode_picker:dir(rtl) .coordinate_copy_menu {
    right: auto;
    left: 0;
}

.coordinate_copy_menu_item {
    display: grid;
    grid-template-columns: 1.15rem minmax(0, 1fr);
    align-items: center;
    width: 100%;
    min-height: 2rem;
    padding: 0.35rem 0.55rem;
    border: 0;
    border-radius: 5px;
    background: transparent;
    color: #0f172a;
    font: inherit;
    text-align: left;
    cursor: pointer;
    box-sizing: border-box;
}

.coordinate_copy_menu_item:hover,
.coordinate_copy_menu_item:focus-visible {
    background: #f1f5f9;
    outline: none;
}

.coordinate_copy_menu_item.selected {
    background: #e8f4ec;
    color: #2f6b43;
    font-weight: 700;
}

.coordinate_copy_menu_check {
    color: #4d8c61;
    font-weight: 800;
}

.coordinate_copy_menu_divider {
    height: 1px;
    margin: 0.35rem 0.2rem;
    background: #e2e8f0;
}

.coordinate_copy_menu_format {
    display: grid;
    gap: 0.35rem;
    padding: 0.2rem 0.25rem 0.15rem;
}

.coordinate_copy_menu_format label {
    color: #334155;
    font-size: 0.75rem;
    font-weight: 700;
}

.coordinate_copy_menu_format input {
    width: 100%;
    height: 2rem;
    padding: 0 0.5rem;
    border: 1px solid #cbd5e1;
    border-radius: 5px;
    outline: none;
    background: #f8fafc;
    color: #0f172a;
    font: inherit;
    font-size: 0.7rem;
    box-sizing: border-box;
}

.coordinate_copy_menu_format input:focus {
    border-color: #4d8c61;
    box-shadow: 0 0 0 2px rgba(77, 140, 97, 0.14);
}

.coordinate_copy_menu_format input::placeholder {
    color: #64748b;
}

.coordinate_copy_menu_hint {
    color: #64748b;
    font-size: 0.64rem;
    line-height: 1.35;
}

.coordinate_jump_fields {
    display: flex;
    align-items: center;
    min-width: 0;
    flex: 1 1 auto;
    align-self: stretch;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity 120ms ease;
}

.coordinate_jump:hover .coordinate_jump_fields,
.coordinate_jump:focus-within .coordinate_jump_fields {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
}

.coordinate_jump.collapsed .coordinate_jump_fields,
.coordinate_jump.collapsed:hover .coordinate_jump_fields,
.coordinate_jump.collapsed:focus-within .coordinate_jump_fields {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
}

.coordinate_jump_fields input {
    min-width: 0;
    height: 100%;
    flex: 1 1 auto;
    border: 0;
    outline: none;
    background: transparent;
    color: #0f172a;
    padding: 0 0.45rem;
    font: inherit;
    box-sizing: border-box;
}

.coordinate_jump_fields input::placeholder {
    color: #64748b;
}

.coordinate_jump button {
    width: 1.6rem;
    height: 100%;
    flex: 0 0 1.6rem;
    border: 0;
    border-left: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #2f6b43;
    cursor: pointer;
}

.coordinate_jump .coordinate_jump_toggle {
    border-left: 0;
}

.coordinate_jump .coordinate_jump_paste {
    color: #475569;
    background: #ffffff;
}

.coordinate_jump button:hover,
.coordinate_jump button:focus-visible {
    background: #e8f4ec;
    outline: none;
}

#tooltip {
    position: absolute;
    pointer-events: none;
    z-index: 500;
}

#map_container :deep(.player_location_marker) {
    filter: drop-shadow(0 1px 2px rgba(15, 23, 42, 0.35));
}

#map_container :deep(.player_location_label) {
    padding: 0.15rem 0.4rem;
    border: 1px solid #6b9b78;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.96);
    color: #244f33;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.16);
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
}

#map_container :deep(.player_location_label::before) {
    border-top-color: #6b9b78;
}


.top{
    position: absolute;
    z-index: 500;
    left: 50%;
    transform: translateX(-50%);
    top: 0.5rem;
}

.bottom {
    position: absolute;
    z-index: 500;
    left: 50%;
    transform: translateX(-50%);
    bottom: 0.5rem;
}

.info {
    padding: 0.3rem;
    padding-left: 1rem;
    padding-right: 1rem;
    border-radius: 1rem;
    background-color: rgba(255, 255, 255, 0.94);
    color: #334155;
    user-select: none;
    margin: 0.2rem;
    border: 1px solid #d6dde5;
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
}

.zoom {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    max-width: calc(100vw - 1rem);
    border-color: #b9d9c5;
    background-color: rgba(248, 252, 250, 0.97);
    color: #2f6b43;
    font-weight: 700;
    cursor: pointer;
}

.zoom:hover {
    background-color: #e8f4ec;
    border-color: #8fc39e;
}

.zoom:focus-visible {
    outline: 3px solid rgba(77, 140, 97, 0.22);
    outline-offset: 2px;
}

.zoom svg {
    flex: 0 0 auto;
}

.teleport {
    color: #334155;
}

.unsupported {
    background-color: rgb(165, 33, 33);
    color: #ffffff;
    border: 2px solid white
}


</style>
